import React from "react";
import { Alert, Button, Descriptions, Spin, Typography } from "antd";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPassportSharePublic, passportShareDownloadUrl } from "../api";
import type { PassportSharePublic } from "../compliance/types";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { TrustStrip } from "../components/TrustStrip";

/** US-403: tokenized read-only passport summary (no Keycloak). */
export function PublicDossierPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [meta, setMeta] = React.useState<PassportSharePublic | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(false);
    void getPassportSharePublic(token)
      .then(setMeta)
      .catch(() => {
        setError(true);
        setMeta(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return null;

  return (
    <div className="public-dossier" data-testid="public-dossier-page">
      <header className="public-dossier__top">
        <Typography.Text className="public-dossier__brand">{t("app.brand")}</Typography.Text>
        <LanguageSwitcher />
      </header>
      <Typography.Title level={2}>{t("investor.dossier.title")}</Typography.Title>
      {loading ? (
        <Spin />
      ) : error || !meta ? (
        <Alert type="error" showIcon message={t("investor.dossier.notFound")} />
      ) : (
        <>
          <Alert type="warning" showIcon message={meta.legal_disclaimer} style={{ marginBottom: 16 }} />
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label={t("investor.dossier.reference")}>
              {meta.contract_reference || meta.case_id}
            </Descriptions.Item>
            <Descriptions.Item label={t("investor.dossier.commodity")}>
              {meta.commodity?.replace(/_/g, " ") || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("investor.dossier.supplier")}>
              {meta.supplier_name || "—"}
              {meta.supplier_country ? ` (${meta.supplier_country})` : ""}
            </Descriptions.Item>
            <Descriptions.Item label={t("investor.dossier.destination")}>
              {meta.destination_country || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("investor.dossier.risk")}>
              {meta.risk_score != null ? meta.risk_score.toFixed(1) : "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("investor.dossier.version")}>
              v{meta.passport_version}
            </Descriptions.Item>
            <Descriptions.Item label={t("investor.dossier.expires")}>
              {new Date(meta.expires_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
          {meta.risk_summary && (
            <Typography.Paragraph style={{ marginTop: 16 }}>{meta.risk_summary}</Typography.Paragraph>
          )}
          <Button
            type="primary"
            href={passportShareDownloadUrl(token)}
            style={{ marginTop: 16 }}
            data-testid="dossier-download"
          >
            {t("investor.dossier.downloadZip")}
          </Button>
        </>
      )}
      <TrustStrip className="public-dossier__trust" />
    </div>
  );
}
